import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CreateStripeCustomerParams,
  PaymentMethods,
  StripeAddress,
  StripeCustomer,
  StripePaymentMethod,
} from '../../types/stripePayments';
import {
  getBarberById,
  getShopOwnerById,
  listCustomerPaymentMethods,
  retrieveStripeCustomer,
} from '../../middleware/registration_service/getRequests';
import AddCardPayment from './AddCardPayment';
import { UK_CITIES } from '../../defaults/Cities';
import { UserRole } from '../../types/role';
import {
  createStripeCustomer,
  detachPaymentMethod,
  updateStripeCustomer,
} from '../../middleware/registration_service/createRequest';

const ACCEPTED_CARDS = ['Visa', 'Mastercard', 'Amex', 'Maestro', 'Discover'];

const schema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  name: Yup.string().required('Full name is required'),
  line1: Yup.string().required('Address line 1 is required'),
  line2: Yup.string(),
  city: Yup.string().required('City is required'),
  country: Yup.string().required('Country is required'),
  postal_code: Yup.string().required('Postcode is required'),
  state: Yup.string(),
});

export default function PaymentDetails() {
  const { getAccessTokenSilently } = useAuth0();
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  /** Stripe customer ID — set after successful customer creation or fetched from backend */
  const [customerId, setCustomerId] = useState<string | null>(null);
  /** True while we check the backend for an existing Stripe customer */
  const [initialLoading, setInitialLoading] = useState(true);
  /** Indicates this is an existing customer (update flow) */
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  /** Default source (card) ID from the Stripe customer */
  const [defaultSource, setDefaultSource] = useState<string | null>(null);
  /** Payment methods list from Stripe */
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  /** Toggle to show the update card form */
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  /** Success message for card add/update */
  const [cardSuccess, setCardSuccess] = useState<string | null>(null);
  /** ID of the payment method currently being detached */
  const [detachingId, setDetachingId] = useState<string | null>(null);

  const role = localStorage.getItem('role');
  const partnerId = localStorage.getItem('reg_partner_id');

  // ── Check for existing Stripe customer on mount ────────────────────────────
  useEffect(() => {
    const fetchExistingCustomer = async () => {
      if (!partnerId) {
        setInitialLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const id = BigInt(partnerId);
        let existingCustomerId: string | null = null;

        if (role === UserRole.BARBER) {
          const res = await getBarberById(id);
          existingCustomerId = res.data?.customerId ?? null;
        } else if (role === UserRole.SHOP_OWNER) {
          const res = await getShopOwnerById(id);
          existingCustomerId = res.data?.customerId ?? null;
        }

        if (existingCustomerId) {
          // Retrieve full Stripe customer details
          const response = await retrieveStripeCustomer(
            existingCustomerId,
            token
          );
          const stripeCustomer: StripeCustomer = response.data;

          setCustomerId(existingCustomerId);
          setIsExistingCustomer(true);

          // Check if customer has a default source (card on file)
          if (stripeCustomer.invoiceSettings?.defaultPaymentMethod) {
            setDefaultSource(
              stripeCustomer.invoiceSettings.defaultPaymentMethod
            );

            // Fetch payment methods to display
            try {
              const customerPaymentMethods = await listCustomerPaymentMethods(
                existingCustomerId,
                token
              );
              const methods: PaymentMethods = customerPaymentMethods.data;
              const sorted = (methods.data ?? []).sort((a, b) => {
                // Default source first
                if (
                  a.id === stripeCustomer.invoiceSettings?.defaultPaymentMethod
                )
                  return -1;
                if (
                  b.id === stripeCustomer.invoiceSettings?.defaultPaymentMethod
                )
                  return 1;
                // Then by most recently added
                return (
                  new Date(b.created).getTime() - new Date(a.created).getTime()
                );
              });
              setPaymentMethods(sorted);
            } catch (pmErr) {
              console.error('Error fetching payment methods:', pmErr);
            }
          }

          // Pre-populate formik with Stripe customer billing address
          const addr: StripeAddress =
            stripeCustomer.address ?? ({} as StripeAddress);
          formik.setValues({
            email: stripeCustomer.email ?? '',
            name: stripeCustomer.name ?? '',
            line1: addr.line1 ?? '',
            line2: addr.line2 ?? '',
            city: addr.city ?? '',
            country: addr.country ?? 'United Kingdom',
            postal_code: addr.postal_code ?? '',
            state: addr.state ?? '',
          });
        }
      } catch (err) {
        console.error('Error fetching existing customer:', err);
        // Non-blocking — fall through to the new-customer form
      } finally {
        setInitialLoading(false);
      }
    };

    fetchExistingCustomer();
  }, [partnerId, role, getAccessTokenSilently]);

  // ── Formik ─────────────────────────────────────────────────────────────────
  const formik = useFormik<
    CreateStripeCustomerParams & {
      line1: string;
      line2?: string;
      city: string;
      country: string;
      postal_code: string;
      state?: string;
    }
  >({
    initialValues: {
      email: '',
      name: '',
      line1: '',
      line2: '',
      city: '',
      country: 'United Kingdom',
      postal_code: '',
      state: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setApiError(null);
      setSuccess(false);
      try {
        const token = await getAccessTokenSilently();
        if (isExistingCustomer && customerId) {
          // ── Update existing Stripe customer (billing address only) ──
          const address = {
            line1: values.line1,
            line2: values.line2 || undefined,
            city: values.city,
            country: values.country,
            postal_code: values.postal_code,
            state: values.state || undefined,
          };
          await updateStripeCustomer(
            customerId,
            {
              address,
              shipping: {
                name: values.name,
                address: {
                  line1: values.line1,
                  line2: values.line2 || undefined,
                  city: values.city,
                  country: values.country,
                  postal_code: values.postal_code,
                  state: values.state || undefined,
                },
              },
            },
            token
          );
          setSuccess(true);
        } else {
          // ── Create new Stripe customer ──
          const params: CreateStripeCustomerParams = {
            email: values.email,
            name: values.name,
            address: {
              line1: values.line1,
              line2: values.line2 || undefined,
              city: values.city,
              country: values.country,
              postal_code: values.postal_code,
              state: values.state || undefined,
            },
            shipping: {
              name: values.name,
              address: {
                line1: values.line1,
                line2: values.line2 || undefined,
                city: values.city,
                country: values.country,
                postal_code: values.postal_code,
                state: values.state || undefined,
              },
            },
          };
          const response = await createStripeCustomer(params);
          const customer: StripeCustomer = response.data;
          const stripeCustomerId = customer.id as string;

          // Store Stripe customer ID so AddCardPayment can initialise a SetupIntent
          setCustomerId(stripeCustomerId);
          setSuccess(true);
        }
      } catch (err: any) {
        setApiError(
          err?.message ?? 'Failed to save customer details. Please try again.'
        );
      }
    },
  });

  // ── Handle successful card add/update ───────────────────────────────────────
  const handleCardSuccess = async () => {
    setCardSuccess(
      showUpdateCard
        ? 'New payment card added successfully.'
        : 'Payment card added successfully.'
    );
    setShowUpdateCard(false);

    // Refresh payment methods and default source
    if (customerId) {
      try {
        const token = await getAccessTokenSilently();
        const response = await retrieveStripeCustomer(customerId, token);
        const customer: StripeCustomer = response.data;
        if (customer.invoiceSettings?.defaultPaymentMethod) {
          setDefaultSource(customer.invoiceSettings.defaultPaymentMethod);
        }

        const customerPaymentMethods = await listCustomerPaymentMethods(
          customerId,
          token
        );
        const methods: PaymentMethods = customerPaymentMethods.data;
        const sorted = (methods.data ?? []).sort(
          (a: any, b: any) => b.created - a.created
        );
        setPaymentMethods(sorted);

        // SetupIntent is now created on demand inside AddCardPayment;
        // no need to refresh latestSetupIntentId here.
      } catch (err) {
        console.error('Error refreshing payment methods:', err);
      }
    }
  };

  // ── Handle detaching a payment method ───────────────────────────────────────
  const handleDetach = async (paymentMethodId: string) => {
    setDetachingId(paymentMethodId);
    setCardSuccess(null);
    setApiError(null);

    try {
      const token = await getAccessTokenSilently();
      await detachPaymentMethod(paymentMethodId, token);

      // Refresh payment methods
      if (customerId) {
        const customerPaymentMethods = await listCustomerPaymentMethods(
          customerId,
          token
        );
        const methods: PaymentMethods = customerPaymentMethods.data;
        if (methods.data && methods.data.length > 0) {
          setDefaultSource(methods.data[0].id);
          await updateStripeCustomer(
            customerId,
            {
              invoiceSettings: {
                defaultPaymentMethod: methods.data[0].id,
              },
            },
            token
          );
        }
        const sorted = (methods.data ?? []).sort(
          (a: any, b: any) => b.created - a.created
        );
        setPaymentMethods(sorted);

        // If no payment methods remain, clear defaultSource
        if (sorted.length === 0) {
          setDefaultSource(null);
        }
      }

      setCardSuccess('Payment method removed successfully.');
    } catch (err: any) {
      setApiError(
        err?.message ?? 'Failed to remove payment method. Please try again.'
      );
    } finally {
      setDetachingId(null);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (initialLoading) {
    return (
      <Container sx={{ mt: '1em' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading payment details…
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: '1em' }}>
      {/* Accepted cards */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {ACCEPTED_CARDS.map((card) => (
          <Chip key={card} label={card} size="small" variant="outlined" />
        ))}
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isExistingCustomer
            ? 'Billing address updated successfully.'
            : 'Customer profile saved successfully.'}
        </Alert>
      )}
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}
      {cardSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setCardSuccess(null)}
        >
          {cardSuccess}
        </Alert>
      )}

      {/* ── Customer details form ── */}
      {(!customerId || isExistingCustomer) && (
        <form onSubmit={formik.handleSubmit} noValidate>
          {/* ── Customer Details ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonOutlineIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Customer Details
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && !!formik.errors.name}
            helperText={formik.touched.name && formik.errors.name}
            sx={{ mb: 2 }}
            InputProps={{ readOnly: isExistingCustomer }}
            disabled={isExistingCustomer}
          />

          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && !!formik.errors.email}
            helperText={formik.touched.email && formik.errors.email}
            sx={{ mb: 2 }}
            InputProps={{ readOnly: isExistingCustomer }}
            disabled={isExistingCustomer}
          />

          <Divider sx={{ my: 2 }} />

          {/* ── Billing Address ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HomeOutlinedIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Billing Address
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Address Line 1"
            name="line1"
            value={formik.values.line1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.line1 && !!formik.errors.line1}
            helperText={formik.touched.line1 && formik.errors.line1}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address Line 2 (optional)"
            name="line2"
            value={formik.values.line2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              select
              label="City"
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && !!formik.errors.city}
              helperText={formik.touched.city && formik.errors.city}
            >
              {UK_CITIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Postcode"
              name="postal_code"
              value={formik.values.postal_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.postal_code && !!formik.errors.postal_code}
              helperText={
                formik.touched.postal_code && formik.errors.postal_code
              }
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.country && !!formik.errors.country}
              helperText={formik.touched.country && formik.errors.country}
            />

            <TextField
              fullWidth
              label="County / State (optional)"
              name="state"
              value={formik.values.state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>

          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={formik.isSubmitting}
            sx={{ mt: 1, mb: 3 }}
            startIcon={
              formik.isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {formik.isSubmitting
              ? 'Saving…'
              : isExistingCustomer
              ? 'Update Billing Address'
              : 'Save Customer Profile'}
          </Button>
        </form>
      )}

      {/* ── Payment Methods on file ── */}
      {customerId && defaultSource && !showUpdateCard && (
        <Box>
          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditCardIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Payment Methods
            </Typography>
          </Box>

          {paymentMethods.length > 0 ? (
            paymentMethods.map((pm: StripePaymentMethod) => (
              <Box
                key={pm.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor:
                    pm.id === defaultSource ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor:
                    pm.id === defaultSource ? 'primary.50' : 'transparent',
                }}
              >
                <CreditCardIcon
                  color={pm.id === defaultSource ? 'primary' : 'action'}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {pm.card?.brand
                      ? pm.card.brand.charAt(0).toUpperCase() +
                        pm.card.brand.slice(1)
                      : 'Card'}{' '}
                    •••• {pm.card?.last4 ?? '****'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expires {String(pm.card?.expMonth).padStart(2, '0')}/
                    {pm.card?.expYear}
                  </Typography>
                </Box>
                {pm.id === defaultSource && (
                  <Chip label="Default" size="small" color="primary" />
                )}
                <Tooltip
                  title={
                    paymentMethods.length <= 1
                      ? 'At least one payment method must remain as default'
                      : 'Remove card'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={
                        detachingId === pm.id || paymentMethods.length <= 1
                      }
                      onClick={() => handleDetach(pm.id)}
                    >
                      {detachingId === pm.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A default payment source is on file.
            </Typography>
          )}

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            startIcon={<CreditCardIcon />}
            onClick={() => setShowUpdateCard(true)}
          >
            Add New Payment Card
          </Button>
        </Box>
      )}

      {/* ── Add / Update Payment Card form ── */}
      {customerId && (!defaultSource || showUpdateCard) && (
        <AddCardPayment
          customerId={customerId}
          isUpdate={!!defaultSource}
          onCancel={defaultSource ? () => setShowUpdateCard(false) : undefined}
          onSuccess={handleCardSuccess}
        />
      )}
    </Container>
  );
}
